// Login form component with OTP verification

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  VStack,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Text,
  Box,
} from '@chakra-ui/react';
import {
  loginSchema,
  otpSchema,
  LoginFormData,
  OTPFormData,
} from '@/client/utils/validationSchemas';
import { CountryCodeSelect } from '@/design-system/components/CountryCodeSelect';
import { FormField } from '@/components/Modal/FormField';
import { otpErrorMessage } from '../otpErrorMessage';

interface LoginFormProps {
  onLogin: (data: LoginFormData) => Promise<void>;
  onVerifyOTP: (mobile: string, countryCode: string, otp: number) => Promise<void>;
}

/**
 * Login form with WhatsApp OTP authentication
 */
export function LoginForm({ onLogin, onVerifyOTP }: LoginFormProps) {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [credentials, setCredentials] = useState<LoginFormData | null>(null);

  // Phone number form
  const phoneForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      country_code: '+91',
      phone_number: '',
    },
  });

  // OTP form
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const handlePhoneSubmit = async (data: LoginFormData) => {
    await onLogin(data);
    setCredentials(data);
    setStep('otp');
  };

  const handleVerifyClick = async () => {
    if (!credentials) return;

    setOtpError(null);
    setIsVerifying(true);

    try {
      const result = otpSchema.safeParse({ otp: otpValue });
      if (!result.success) {
        setOtpError(result.error.issues[0].message);
        return;
      }

      await onVerifyOTP(credentials.phone_number, credentials.country_code, parseInt(otpValue, 10));
    } catch (error) {
      setOtpError(otpErrorMessage(error));
      setOtpValue('');
    } finally {
      setIsVerifying(false);
    }
  };

  if (step === 'otp') {
    return (
      <VStack spacing={6} align="stretch">
        <Text textAlign="center" fontSize="lg">
          Enter the 6-digit code sent to
        </Text>
        <Text textAlign="center" fontWeight="bold" fontSize="xl">
          {credentials?.country_code} {credentials?.phone_number}
        </Text>

        <FormControl isInvalid={!!otpError}>
          <FormLabel>OTP Code</FormLabel>
          <Input
            data-testid="login-otp-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            size="lg"
            textAlign="center"
            letterSpacing="0.3em"
            autoFocus
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: 'border-hover' }}
            _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
          />
          {otpError && (
            <Box mt={2}>
              <FormErrorMessage data-testid="login-otp-error">{otpError}</FormErrorMessage>
            </Box>
          )}
        </FormControl>

        <Button
          data-testid="login-verify-otp-btn"
          colorScheme="brand"
          size="lg"
          isLoading={isVerifying}
          loadingText="Verifying..."
          isDisabled={otpValue.length < 6}
          onClick={handleVerifyClick}
        >
          Verify OTP
        </Button>

        <Button
          variant="link"
          onClick={() => {
            setStep('phone');
            setOtpValue('');
            setOtpError(null);
          }}
          size="sm"
          alignSelf="center"
        >
          Change phone number
        </Button>
      </VStack>
    );
  }

  return (
    <form onSubmit={phoneForm.handleSubmit(handlePhoneSubmit)}>
      <VStack spacing={4} align="stretch">
        <FormControl isInvalid={!!phoneForm.formState.errors.country_code}>
          <FormLabel>Country Code</FormLabel>
          <CountryCodeSelect
            value={phoneForm.watch('country_code')}
            onChange={(value) => phoneForm.setValue('country_code', value)}
            error={phoneForm.formState.errors.country_code?.message}
            testId="login-country-code"
          />
        </FormControl>

        <FormField
          label="Phone Number"
          name="phone_number"
          register={phoneForm.register}
          error={phoneForm.formState.errors.phone_number}
          type="tel"
          placeholder="Enter phone number"
          required
          testId="login-mobile-input"
        />

        <Button
          data-testid="login-request-otp-btn"
          type="submit"
          colorScheme="brand"
          size="lg"
          isLoading={phoneForm.formState.isSubmitting}
          loadingText="Sending OTP..."
          mt={4}
        >
          Send OTP
        </Button>
      </VStack>
    </form>
  );
}
